import { db } from '../../firebase';
import { UserPath } from '../types/userPath';
import { collection, getDocs, setDoc, updateDoc, deleteDoc, doc, query, getDoc } from 'firebase/firestore';
import { parseContent, parseContents } from '../utils';
import { parseUserPath, parseUserPaths } from './parser';
import { ProjectErrorType } from '../../utils/errors/types';
import { getProjectErrorFromRawError, isProjectError } from '../../utils/errors';
import { CreateUserPathDto, UpdateUserPathDto } from '../typeDtos/userPath';

const COLLECTION = 'aluxHomeUserPaths';

export const getAllUserPaths = async (): Promise<UserPath[]> => {
  try {
    const _query = query(collection(db, COLLECTION));
    const rawResult = await getDocs(_query);
    const parsed = parseContents(rawResult);
    const userPaths = await parseUserPaths(parsed);
    return userPaths;
  } catch (error) {
    console.log(`[USER PATHS] Error at getAllUserPaths`, error);
    return [];
  }
};

export const getUserPathById = async (id: string) => {
  try {
    const _query = await getDoc(doc(db, COLLECTION, id));
    if (!_query.exists()) {
      console.log('No such document!');
      return undefined;
    }
    const result = await parseUserPath(parseContent(_query));
    const resultError = isProjectError(result);
    if (!resultError) {
      return result as UserPath;
    }
  } catch (error) {
    console.log(`[USER PATHS] Error at getUserPathById with id ${id}:`, error);
    return undefined;
  }
};

export const createUserPath = async (
  data: CreateUserPathDto,
): Promise<ProjectErrorType | { success: boolean; id: string }> => {
  try {
    const existing = await getDoc(doc(db, COLLECTION, data.id));
    if (existing.exists()) {
      return getProjectErrorFromRawError(new Error(`A user path with ID "${data.id}" already exists.`));
    }

    const nowTs = new Date().getTime();
    const { id, ...rest } = data;
    const _data = {
      ...rest,
      createdAt: nowTs,
      updatedAt: nowTs,
    };
    await setDoc(doc(db, COLLECTION, id), _data);
    return { success: true, id };
  } catch (e) {
    console.log('[USER PATHS] Error at createUserPath:', e);
    return getProjectErrorFromRawError(e as Error);
  }
};

export const updateUserPath = async (data: UpdateUserPathDto): Promise<ProjectErrorType | { success: boolean }> => {
  try {
    const nowTs = new Date().getTime();
    const { id, ...rest } = data;
    const _data = { ...rest, updatedAt: nowTs };
    const docRef = doc(db, COLLECTION, id);
    await updateDoc(docRef, _data);
    return { success: true };
  } catch (e) {
    console.log('[USER PATHS] Error at updateUserPath:', e);
    return getProjectErrorFromRawError(e as Error);
  }
};

export const deleteUserPath = async (id: string): Promise<ProjectErrorType | { success: boolean }> => {
  try {
    const docRef = doc(db, COLLECTION, id);
    await deleteDoc(docRef);
    return { success: true };
  } catch (e) {
    console.log('[USER PATHS] Error at deleteUserPath:', e);
    return getProjectErrorFromRawError(e as Error);
  }
};
